import {
  AfterViewInit,
  ContentChild,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { animationFrameScheduler, from, fromEvent, mergeMap, Subscription } from 'rxjs';
import { map, subscribeOn, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DragHandleDirective } from './drag-handle.directive';
import { CSS_SIZE_UNIT_PX } from "../../../../../external/models/css-consts";

interface MouseCoords {
  x: number;
  y: number;
}

@Directive({
  selector: '[txtbDraggable]',
  standalone: false,
})
export class DraggableDirective implements AfterViewInit, OnDestroy {
  subscription = new Subscription();
  @Input() txtbDraggable: string | boolean = true;
  @Output() txtbDragStart = new EventEmitter<void>();
  @Output() txtbDragEnd = new EventEmitter<void>();
  @ContentChild(DragHandleDirective) txtbDragHandle!: DragHandleDirective;
  @HostBinding('style.will-change') willChange = 'transform';

  constructor(private elementRef: ElementRef) {}

  ngAfterViewInit(): void {
    if (this.txtbDragHandle || typeof this.txtbDragHandle === 'string') {
      const dragHandle =
        this.txtbDragHandle?.elementRef.nativeElement || this.elementRef.nativeElement;
      const mousedown$ = from(['mousedown', 'touchstart']).pipe(
        mergeMap((event) => fromEvent<MouseEvent | TouchEvent>(dragHandle, event))
      );
      const mousemove$ = from(['mousemove', 'touchmove']).pipe(
        mergeMap((event) => fromEvent<MouseEvent | TouchEvent>(document, event))
      );
      const mouseup$ = from(['mouseup', 'touchend']).pipe(
        mergeMap((event) => fromEvent<MouseEvent | TouchEvent>(document, event))
      );

      const drag$ = mousedown$.pipe(
        tap(() => this.txtbDragStart.emit()),
        map((event) => {
          const startCoords = this.getMousePosition(event);
          const transform = this.elementRef.nativeElement.style.transform;
          if (transform) {
            const transformValues = transform.match(/[-]?\d+/g);

            return {
              x: startCoords.x - transformValues[0],
              y: startCoords.y - transformValues[1],
            };
          }

          return startCoords;
        }),
        switchMap((startCoords) => {
          return mousemove$.pipe(
            map((move) => {
              move.preventDefault();
              const moveCoords = this.getMousePosition(move);

              return {
                left: moveCoords.x - startCoords.x,
                top: moveCoords.y - startCoords.y,
              };
            }),
            takeUntil(mouseup$.pipe(tap(() => this.txtbDragEnd.emit())))
          );
        }),
        subscribeOn(animationFrameScheduler)
      );

      this.subscription.add(
        drag$.subscribe(
          (pos) =>
            (this.elementRef.nativeElement.style.transform = `translateX(${pos.left}${CSS_SIZE_UNIT_PX}) translateY(${pos.top}${CSS_SIZE_UNIT_PX})`)
        )
      );
    }
  }

  getMousePosition(e: MouseEvent | TouchEvent): MouseCoords {
    const { clientX: x, clientY: y } =
      window.TouchEvent && e instanceof TouchEvent ? e.changedTouches[0] : (e as MouseEvent);
    return { x, y };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
